/**
 * The props on the planet: plants, stones, deposits, rubble, machines and the hub, as
 * instanced meshes rebuilt only when tiles (or the extractors on them) change. A pop rescales just
 * the tile that changed; turning parts turn; an extractor's core fills.
 */
import { KIND, type Kind } from '@bramvasterd/terrella-rules';
import * as THREE from 'three';

import { isShown } from '../planet/surface';
import { KIND_HEIGHT, radiusOf, STEP, type PlanetView } from '../planet/view';
import type { Look } from '../style/look';
import { propKinds } from './catalog';
import { fillForests, placeFlora } from './flora';
import { centerOf } from './geometry';
import { createFills, placeMachine } from './machines';
import { placeGround, placeRubble } from './ground';
import { placeHub } from './hub';
import { createInstances } from './instances';
import { createSpinners } from './spinners';

export type Detail = 'full' | 'low';

export interface Props {
  /** Rebuild every prop from the planet's tiles. Low detail (the welcome planet) places one flower per grass tile. */
  build(view: PlanetView, detail?: Detail): void;
  /** The latest view, for what changes without a rebuild (what an extractor holds). */
  track(view: PlanetView): void;
  pop(tile: number, now: number): void;
  /** Advance pops, turning parts and extractors filling; returns whether anything keeps moving. */
  animate(now: number, serverNow: number | null, reduced: boolean): boolean;
  /** The art arrived (docs/ART.md): each kind it has is drawn with its model from now on. */
  setArt(geometries: ReadonlyMap<string, THREE.BufferGeometry>): void;
}

/** Fruit saplings and fruit trees are gone from the game; old data draws as their plain kinds. */
const plain = (kind: Kind): Kind =>
  kind === KIND.FRUIT_SAPLING ? KIND.SAPLING : kind === KIND.FRUIT_TREE ? KIND.TREE : kind;

const _c = new THREE.Vector3();

export function createProps(parent: THREE.Group, look: Look = 'classic'): Props {
  const instances = createInstances(parent, propKinds(look));
  const spinners = createSpinners(instances);
  const fills = createFills(instances);
  return {
    track(view) {
      fills.track(view);
    },
    build(view, detail = 'full') {
      const flowers = detail === 'full' ? 3 : 1;
      instances.begin(radiusOf(view.grid.subdivisions));
      spinners.clear();
      fills.clear();
      for (let i = 0; i < view.grid.count; i++) {
        if (!isShown(view.shown, i)) continue;
        const kind = plain((view.kind[i] ?? 0) as Kind);
        const h0 = (view.elev[i] ?? 0) * STEP + KIND_HEIGHT[kind] - 0.004;
        instances.at(i, h0);
        centerOf(view, i, _c);
        // a machine first: on grass (an infuser) nothing grows through it; on rubble (an
        // extractor) the cracked top shows round it
        if (placeMachine(instances, spinners, fills, view, i, h0)) continue;
        if (placeFlora(instances, view, i, _c, kind, flowers, look)) continue;
        if (view.shattered?.[i]) {
          placeRubble(instances, i, _c, kind);
          continue;
        }
        if (placeGround(instances, i, _c, kind, (view.drawn?.[i] ?? 0) > 0)) continue;
        placeHub(instances, _c, kind);
      }
      fillForests(instances, view, look);
      instances.end();
    },
    pop: (tile, now) => instances.pop(tile, now),
    animate(now, serverNow, reduced) {
      const popping = instances.animatePops(now);
      // cores fill over minutes: followed every frame, but not a reason for full speed (a full
      // one pauses its ring, so it goes first)
      fills.animate(serverNow);
      const turning = spinners.animate(now, reduced);
      instances.flush();
      return popping || turning;
    },
    setArt(geometries) {
      for (const [name, geometry] of geometries) instances.setGeometry(name, geometry);
    },
  };
}
